import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    AIFeature,
    checkIsEnterpriseProductKey,
    checkIsUnrestrictedProductKey,
    type FeatureUpgrade,
    getFeatureDefinition,
    getSiteProductDefinition,
    getUpgradeRequirement,
    SITE_FEATURES_MAP,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { stopPropagationProps, toast } from '@gitbook/spine-react';

import { useUpgradeDialogContext, type UpgradeDialogState } from '../../Components';
import type { NavigateFunction } from '../../navigation/types';
import { useNavigation } from '../../navigation/useNavigation';
import {
    useOpenSiteUpgradeModal,
    type SiteUpgradeModalState,
} from '../../Screens/Site/components/Plan/useSiteUpgradeModal';
import { apiClient, apiQuery } from '../api';
import { ContactSupportReasons, useContactSupport, type ContactSupportFn } from '../support';
import type {
    AIDisabledAction,
    AIPausedAction,
    AIRestrictedAction,
    FeatureUpgradeAction,
    SiteUpgradeAction,
    UpgradeAction,
} from './types';

const REQUEST_UPGRADE_NOTICE = {
    kind: 'upgrade',
    title: 'Upgrade requested',
    description:
        "We've notified the organization administrator to ask them to upgrade the account.",
    duplicateTitle: 'Upgrade already requested',
    duplicateDescription:
        "We've already notified the organization administrator to ask them to upgrade the account.",
    error: 'Error while requesting upgrade',
};

const REQUEST_SITE_UPGRADE_NOTICE = {
    title: 'Upgrade requested',
    description: "We've notified the organization administrators to ask them to upgrade the site.",
    duplicateTitle: 'Upgrade already requested',
    duplicateDescription:
        "We've already notified the organization administrators to ask them to upgrade the site.",
    error: 'Error while requesting upgrade',
};

// Sites whose upgrade was already requested, so repeated presses don't notify the administrators again.
const requestedSiteUpgrades = new Set<string>();

const REQUEST_ENABLE_AI_NOTICE = {
    kind: 'enable-ai',
    title: 'Request sent',
    description: "We've asked the organization administrator to enable GitBook AI.",
    duplicateTitle: 'Request already sent',
    duplicateDescription:
        "We've already asked the organization administrator to enable GitBook AI.",
    error: 'Error while sending the request',
};

const REQUEST_RESUME_AI_NOTICE = {
    kind: 'resume-ai',
    title: 'Request sent',
    description: "We've asked the organization administrator to resume GitBook AI.",
    duplicateTitle: 'Request already sent',
    duplicateDescription:
        "We've already asked the organization administrator to resume GitBook AI.",
    error: 'Error while sending the request',
};

export type FeatureUpgradeActionInput = {
    organization: api.Organization;
} & (
    | { featureKey: api.OrganizationFeature; site?: never }
    | { featureKey: api.SiteFeatureKey; site: api.Site }
);

export interface UpgradeActionContext {
    openUpgradeDialog: (state: UpgradeDialogState) => void;
    openAIRestrictedDialog: () => void;
    openSiteUpgradeModal: (site: api.Site, state: SiteUpgradeModalState) => void;
    contactSupport: ContactSupportFn;
    navigate: NavigateFunction;
    preview?: api.OperationSpec<'previewSitePlanUpdate'>['ResponseBody'] | null;
    /** The requirement already resolved for the input, so it is not resolved twice. */
    requirement?: FeatureUpgrade;
}

/** Build button props and callbacks for a feature's required upgrade; no React hooks are called. */
export function getUpgradeAction(
    input: FeatureUpgradeActionInput & { site?: never },
    context: UpgradeActionContext
): UpgradeAction | undefined;
export function getUpgradeAction(
    input: FeatureUpgradeActionInput,
    context: UpgradeActionContext
): FeatureUpgradeAction | undefined;
export function getUpgradeAction(
    input: FeatureUpgradeActionInput,
    context: UpgradeActionContext
): FeatureUpgradeAction | undefined {
    const { organization } = input;
    const upgrade = 'requirement' in context ? context.requirement : getUpgradeRequirement(input);
    if (!upgrade) {
        return undefined;
    }
    if (upgrade.type === 'organization') {
        const blockedFeature = upgrade.featureKey;
        const requiredPlanTitle = upgrade.plan?.title;
        const featureTitle = getFeatureDefinition(blockedFeature)?.title ?? 'this feature';
        if (!organization.permissions.admin) {
            return getRequestUpgradeAction({ organization, blockedFeature, featureTitle });
        }
        if (upgrade.plan && checkIsEnterpriseProductKey(upgrade.plan.key)) {
            return getContactSalesAction({ organization, blockedFeature, requiredPlanTitle });
        }
        if (organization.trial.status === api.BillingTrialStatus.None) {
            return getStartTrialAction({ organization, requiredPlanTitle, featureTitle });
        }
        return {
            ...stopPropagationProps,
            _type: 'upgrade',
            kind: 'upgrade',
            leadingIcon: Icon.Upgrade,
            label: `Upgrade to use ${featureTitle}`,
            badgeLabel: 'Upgrade',
            plan: requiredPlanTitle,
            tooltip: { label: 'Upgrade to unlock this feature' },
            onPress: async () =>
                context.openUpgradeDialog({
                    organization,
                    targetProduct: api.BillingProduct.Pro2024,
                    reason: blockedFeature,
                }),
        };
    }
    if (upgrade.type === 'ai-restricted') {
        return organization.permissions.admin
            ? getAIRestrictedAction({ organization, navigate: context.navigate })
            : getAIRestrictedInfoAction({ openDialog: context.openAIRestrictedDialog });
    }
    if (upgrade.type === 'ai-paused') {
        return organization.permissions.admin
            ? getAIPausedAction({ organization, navigate: context.navigate })
            : getRequestResumeAIAction({ organization });
    }
    if (upgrade.type === 'ai-disabled') {
        const definition = input.site
            ? SITE_FEATURES_MAP[input.featureKey]
            : getFeatureDefinition(input.featureKey);
        const featureTitle =
            definition?.aiFeature === AIFeature.Agent ? 'AI Agent' : definition?.title;
        return organization.permissions.admin
            ? getEnableAIAction({ organization, featureTitle, navigate: context.navigate })
            : getRequestEnableAIAction({ organization });
    }
    const site = input.site;
    if (!site) {
        return undefined;
    }
    if (upgrade.type === 'contact-support') {
        return {
            ...stopPropagationProps,
            _type: 'contact-support',
            kind: 'secondary',
            leadingIcon: Icon.Send,
            label: 'Get access',
            tooltip: { label: 'This feature requires a site plan. Contact support to learn more.' },
            onPress: async () =>
                context.contactSupport({
                    reason: ContactSupportReasons.REQUEST_FEATURE,
                    subject: `I'm interested in ${SITE_FEATURES_MAP[upgrade.featureKey].title}`,
                }),
        };
    }
    const sitePlan = getSiteProductDefinition(upgrade.plan);

    if (!site.permissions.admin) {
        return getRequestSiteUpgradeAction({
            organization,
            site,
            featureKey: upgrade.featureKey,
            plan: sitePlan.title,
        });
    }

    const buttonProps: Omit<SiteUpgradeAction, 'label'> = {
        ...stopPropagationProps,
        _type: 'site-upgrade',
        plan: sitePlan.title,
        kind: 'upgrade',
        leadingIcon: Icon.Upgrade,
        onPress: async () => {
            if (checkIsUnrestrictedProductKey(organization.plan)) {
                await toast.catch(async () => {
                    await apiClient.sites.updateSitePlan(organization.id, site.id, {
                        plan: upgrade.plan,
                        reason: upgrade.featureKey,
                    });
                    toast.success(`Your site was switched to ${sitePlan.title}.`);
                }, 'Error while updating site plan');
                return;
            }
            context.openSiteUpgradeModal(site, { tab: 'plans', reason: upgrade.featureKey });
        },
    };
    if (context.preview?.type === 'go-ahead') {
        return {
            ...buttonProps,
            label: `Switch to ${sitePlan.title}`,
            kind: 'primary',
            leadingIcon: undefined,
        };
    }
    if (context.preview?.type === 'trial-available') {
        return { ...buttonProps, label: 'Start a 14-day trial' };
    }
    return { ...buttonProps, label: `Upgrade to ${sitePlan.title}` };
}

/** Supply React context and site billing preview data to getUpgradeAction. */
export function useGetUpgradeAction(
    input: FeatureUpgradeActionInput & { site?: never }
): UpgradeAction | undefined;
export function useGetUpgradeAction(
    input: FeatureUpgradeActionInput | undefined
): FeatureUpgradeAction | undefined;
export function useGetUpgradeAction(
    input: FeatureUpgradeActionInput | undefined
): FeatureUpgradeAction | undefined {
    const organization = input?.organization;
    const site = input?.site;
    const { openUpgradeDialog, openAIRestrictedDialog } = useUpgradeDialogContext();
    const openSiteUpgradeModal = useOpenSiteUpgradeModal();
    const contactSupport = useContactSupport();
    const { navigate } = useNavigation();
    const requirement = useMemo(
        // oxlint-disable-next-line react/react-compiler -- Depend on input fields while preserving its discriminated union.
        () => (input ? getUpgradeRequirement(input) : undefined),
        [input?.organization, input?.site, input?.featureKey]
    );
    const { data: preview } = useSuspenseQuery(
        apiQuery(
            'previewSitePlanUpdate',
            organization && site && requirement?.type === 'site' && site.permissions.admin
                ? { organizationId: organization.id, siteId: site.id, plan: requirement.plan }
                : skipToken,
            { nullOnError: true }
        )
    );
    return useMemo(
        () =>
            // oxlint-disable-next-line react/react-compiler -- Depend on input fields while preserving its discriminated union.
            input
                ? getUpgradeAction(input, {
                      openUpgradeDialog,
                      openAIRestrictedDialog,
                      openSiteUpgradeModal,
                      contactSupport,
                      navigate,
                      preview,
                      requirement,
                  })
                : undefined,
        [
            input?.organization,
            input?.site,
            input?.featureKey,
            requirement,
            openUpgradeDialog,
            openAIRestrictedDialog,
            openSiteUpgradeModal,
            contactSupport,
            navigate,
            preview,
        ]
    );
}

function getContactSalesAction({
    organization,
    blockedFeature,
    requiredPlanTitle,
}: {
    organization: api.Organization;
    blockedFeature: api.OrganizationFeature;
    requiredPlanTitle: string | undefined;
}): UpgradeAction {
    return {
        ...stopPropagationProps,
        _type: 'contact-sales',
        label: 'Contact us',
        plan: requiredPlanTitle,
        kind: 'primary',
        leadingIcon: Icon.Send,
        href: 'mailto:csm@gitbook.com',
        target: '_blank',
        onPress: async () => {
            analytics.logEvent(
                analytics.createOrgEvent(organization.id, {
                    type: 'organization_open_enterprise_cta',
                    currentFeaturesSet: organization.plan,
                    reason: blockedFeature,
                })
            );
        },
    };
}

function getStartTrialAction({
    organization,
    requiredPlanTitle,
    featureTitle,
}: {
    organization: api.Organization;
    requiredPlanTitle: string | undefined;
    featureTitle: string;
}): UpgradeAction {
    return {
        ...stopPropagationProps,
        _type: 'upgrade',
        tooltip: {
            label: 'Start a 14-day free trial',
        },
        label: `Try ${featureTitle}`,
        badgeLabel: 'Start trial',
        plan: requiredPlanTitle,
        onPress: async () => {
            await toast.catch(async () => {
                await apiClient.billing.upgradeOrganizationPlan(organization.id, {
                    product: api.BillingProduct.Pro2024,
                    interval: api.BillingInterval.Monthly,
                    reason: 'upgrade_cta_trial',
                });
                toast('Trial started', {
                    description: 'Your 14-day free trial has been activated.',
                });
            }, 'Error while starting trial');
        },
        kind: 'upgrade',
        leadingIcon: Icon.Upgrade,
    };
}

const getRequestUpgradeCallback = (() => {
    // One notification per organization and request kind, so an AI request does not swallow a
    // later plan request.
    const alreadyNotified = new Set<string>();

    return function createRequestUpgradeCallback({
        organization,
        blockedFeature,
        notice = REQUEST_UPGRADE_NOTICE,
    }: {
        organization: api.Organization;
        blockedFeature: api.OrganizationFeature;
        /** Toast copy once the administrators are notified. */
        notice?: typeof REQUEST_UPGRADE_NOTICE;
    }) {
        return async () => {
            const key = `${organization.id}:${notice.kind}`;
            if (alreadyNotified.has(key)) {
                toast(notice.duplicateTitle, { description: notice.duplicateDescription });
                return;
            }

            alreadyNotified.add(key);
            analytics.logEvent(
                analytics.createOrgEvent(organization.id, {
                    type: 'organization_request_upgrade',
                    currentFeaturesSet: organization.plan,
                    reason: blockedFeature,
                })
            );

            await toast.catch(async () => {
                await apiClient.organizations.requestOrganizationUpgrade(organization.id, {
                    featureKey: blockedFeature,
                });
                toast(notice.title, { description: notice.description });
            }, notice.error);
        };
    };
})();

function getRequestSiteUpgradeAction({
    organization,
    site,
    featureKey,
    plan,
}: {
    organization: api.Organization;
    site: api.Site;
    featureKey: api.SiteFeatureKey;
    plan: string;
}): SiteUpgradeAction {
    return {
        ...stopPropagationProps,
        _type: 'site-upgrade',
        plan,
        kind: 'upgrade',
        leadingIcon: Icon.Upgrade,
        label: `Request upgrade to use ${SITE_FEATURES_MAP[featureKey].title}`,
        badgeLabel: 'Upgrade',
        tooltip: { label: 'Notify and ask your administrator to upgrade this site' },
        onPress: async () => {
            const notice = REQUEST_SITE_UPGRADE_NOTICE;
            if (requestedSiteUpgrades.has(site.id)) {
                toast(notice.duplicateTitle, { description: notice.duplicateDescription });
                return;
            }

            requestedSiteUpgrades.add(site.id);
            await toast.catch(
                async () => {
                    await apiClient.sites.requestSiteUpgrade(organization.id, site.id, {
                        featureKey,
                    });
                    toast(notice.title, { description: notice.description });
                },
                notice.error,
                // A failed request notified no one, so it must not block a retry.
                { onReject: () => requestedSiteUpgrades.delete(site.id) }
            );
        },
    };
}

function getAIPausedAction({
    organization,
    navigate,
}: {
    organization: api.Organization;
    navigate: NavigateFunction;
}): AIPausedAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-paused',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: 'AI paused',
        badgeLabel: 'AI paused',
        tooltip: {
            label: 'AI is paused because credits are low. Add credits or disable automatic pausing in billing settings.',
        },
        onPress: async () =>
            navigate({
                current: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab: { type: 'plans' },
                },
            }),
    };
}

function getRequestResumeAIAction({
    organization,
}: {
    organization: api.Organization;
}): AIPausedAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-paused',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: 'Ask an administrator',
        badgeLabel: 'AI paused',
        tooltip: {
            label: 'AI is paused because credits are low. Notify and ask your administrator to resume AI for this organization.',
        },
        onPress: getRequestUpgradeCallback({
            organization,
            blockedFeature: api.OrganizationFeature.Ai,
            notice: REQUEST_RESUME_AI_NOTICE,
        }),
    };
}

function getAIRestrictedAction({
    organization,
    navigate,
}: {
    organization: api.Organization;
    navigate: NavigateFunction;
}): AIRestrictedAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-restricted',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: 'View billing',
        badgeLabel: 'AI unavailable',
        tooltip: { label: "AI can't be enabled for this organization." },
        onPress: async () =>
            navigate({
                current: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab: { type: 'plans' },
                },
            }),
    };
}

function getAIRestrictedInfoAction({ openDialog }: { openDialog: () => void }): AIRestrictedAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-restricted',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: 'Learn more',
        badgeLabel: 'AI unavailable',
        tooltip: { label: "AI can't be enabled for this organization." },
        onPress: async () => openDialog(),
    };
}

function getEnableAIAction({
    organization,
    featureTitle,
    navigate,
}: {
    organization: api.Organization;
    featureTitle: string | undefined;
    navigate: NavigateFunction;
}): AIDisabledAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-disabled',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: `Enable ${featureTitle ?? 'AI'}`,
        badgeLabel: `Enable ${featureTitle ?? 'AI'}`,
        tooltip: {
            label: 'This AI feature is disabled for this organization. Enable it in the organization settings.',
        },
        // The AI switch lives in the general settings, independently of Agent eligibility.
        onPress: async () =>
            navigate({
                current: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab: { type: 'profile' },
                },
            }),
    };
}

function getRequestEnableAIAction({
    organization,
}: {
    organization: api.Organization;
}): AIDisabledAction {
    return {
        ...stopPropagationProps,
        _type: 'ai-disabled',
        kind: 'secondary',
        leadingIcon: Icon.AI,
        label: 'Ask an administrator',
        badgeLabel: 'Enable AI',
        tooltip: {
            label: 'Notify and ask your administrator to enable this AI feature for this organization',
        },
        onPress: getRequestUpgradeCallback({
            organization,
            blockedFeature: api.OrganizationFeature.Ai,
            notice: REQUEST_ENABLE_AI_NOTICE,
        }),
    };
}

function getRequestUpgradeAction({
    organization,
    blockedFeature,
    featureTitle,
}: {
    organization: api.Organization;
    blockedFeature: api.OrganizationFeature;
    featureTitle: string;
}): UpgradeAction {
    return {
        ...stopPropagationProps,
        _type: 'upgrade',
        tooltip: {
            label: 'Notify and ask your administrator to upgrade this organization',
        },
        label: `Request upgrade to use ${featureTitle}`,
        badgeLabel: 'Upgrade',
        plan: 'Upgrade',
        onPress: getRequestUpgradeCallback({ organization, blockedFeature }),
        kind: 'upgrade',
        leadingIcon: Icon.Upgrade,
    };
}
